import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstanceWithoutInterceptor from "../axiosWithoutInterceptor";
import { updateJobsWithPagination } from "../features/settings/iamEnablement/EnablementUtil";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";

// Helper function to parse API response with various formats
const parseApiResponse = (response, defaultItems = []) => {
    if (!response) {
        return { items: defaultItems, totalCount: 0 };
    }

    try {
        // Handle direct array response
        if (Array.isArray(response)) {
            return {
                items: response,
                totalCount: response.length,
            };
        }

        // Handle object with value property (most common)
        if (response.value && Array.isArray(response.value)) {
            return {
                items: response.value,
                totalCount: response.recordsetCount || response.count || response.totalCount || 0,
            };
        }

        // Handle object with items property
        if (response.items && Array.isArray(response.items)) {
            return {
                items: response.items,
                totalCount: response.recordsetCount || response.count || response.totalCount || 0,
            };
        }

        // Handle object with specific property names (users, companies, list)
        const itemsArray = response.users || response.companies || response.list || [];
        if (Array.isArray(itemsArray)) {
            return {
                items: itemsArray,
                totalCount:
                    response.count ||
                    response.totalCount ||
                    response.recordsetCount ||
                    itemsArray.length,
            };
        }

        // Fallback for unexpected formats - log to application monitoring
        return { items: defaultItems, totalCount: 0 };
    } catch {
        // Error handling - return safe defaults without breaking the application
        return { items: defaultItems, totalCount: 0 };
    }
};

const initialState = {
    accounts: {
        data: [],
        totalCount: 0,
        isLoading: false,
        error: null,
    },
    companies: {
        data: [],
        recordsetCount: null, // null = not fetched yet, 0 = fetched but empty
        isLoading: false,
        error: null,
        paginate: {
            pageNumber: 1,
            startIndex: 0,
            endIndex: 19,
            rowsPerPage: 20,
            totalRecords: 0,
        },
    },
    users: {
        data: [],
        recordsetCount: null, // null = not fetched yet, 0 = fetched but empty
        isLoading: false,
        error: null,
        searchTerm: "",
        paginate: {
            pageNumber: 1,
            startIndex: 0,
            endIndex: 19,
            rowsPerPage: 20,
            totalRecords: 0,
        },
    },
    userGrants: {
        data: [],
        isLoading: false,
        error: null,
    },
    enablement: {
        isLoading: false,
        error: null,
        lastJobId: null,
        lastAvaTaxAccountId: null,
    },
    usersEnablement: {
        isLoading: false,
        error: null,
        lastJobId: null,
        lastAvaTaxAccountId: null,
    },
    jobLogs: {
        data: null,
        isLoading: false,
        error: null,
    },
    userScopeClaims: {
        data: { subjectId: null, claims: [] },
        isLoading: false,
        error: null,
    },
    // Assign grants for a selected user
    assignGrants: {
        isLoading: false,
        error: null,
        success: false,
    },
    // Assign ECM aspect to company
    assignEcmAspect: {
        isLoading: false,
        error: null,
        success: false,
        companyId: null,
    },
    // Assign ECM aspect to all companies
    assignEcmAspectBulk: {
        isLoading: false,
        error: null,
        success: false,
    },
    // Remove grants from a selected user
    removeGrants: {
        isLoading: false,
        error: null,
        success: false,
    },
    // Toggle entitlement status
    toggleEntitlement: {
        isLoading: false,
        error: null,
        success: false,
    },
    jobs: {
        data: [],
        isLoading: false,
        error: null,
        paginate: {
            pageNumber: 0,
            startIndex: 0,
            endIndex: 9,
            rowsPerPage: 10,
            totalRecords: 0,
        },
        statusFilter: "A", // "A" = All jobs
    },
    jobStatus: {
        isLoading: false,
        error: null,
    },
    jobIds: {
        ids: [],
    },
};

export const fetchIamDsAccounts = createAsyncThunk(
    "iamDs/fetchAccounts",
    async () => {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3Url("iam-enablement/iamds-enabled-accounts"),
            {
                withCredentials: true,
            }
        );
        return response.data;
    },
    {
        condition: (forceRefresh, { getState }) => {
            const state = getState();
            const { accounts } = state.iamDs;

            // Don't fetch if already loading
            if (accounts.isLoading) {
                return false;
            }

            // If force refresh is requested, always fetch
            if (forceRefresh) {
                return true;
            }

            // Don't fetch if we already have data and no error
            if (accounts.data && accounts.data.length > 0 && !accounts.error) {
                return false;
            }

            return true;
        },
    }
);

export const fetchTenantCompanies = createAsyncThunk(
    "iamDs/fetchTenantCompanies",
    async ({ tenantId, pageSize = 20, pageNumber = 1, sortColumn = null }) => {
        const skip = (pageNumber - 1) * pageSize;
        const url = buildApiV3UrlWithQuery(
            `iam-enablement/tenants/${tenantId}/iamds-enabled-companies`,
            null, // filter
            null, // include
            pageSize, // top
            skip, // skip
            sortColumn
        );
        const response = await axiosInstanceWithoutInterceptor.get(url, {
            withCredentials: true,
        });
        return {
            data: response.data,
            pageSize,
            pageNumber,
            skip,
        };
    },
    {
        condition: (_, { getState }) => {
            const state = getState();
            return !state.iamDs.companies.isLoading;
        },
    }
);

export const fetchIamDsUsers = createAsyncThunk("iamDs/fetchUsers", async () => {
    const response = await axiosInstanceWithoutInterceptor.get(
        buildApiV3Url("iam-enablement/iamds-enabled-users"),
        {
            withCredentials: true,
        }
    );
    return response.data;
});

export const fetchTenantUsers = createAsyncThunk(
    "iamDs/fetchTenantUsers",
    async ({
        tenantId,
        avaTaxAccountId,
        pageSize = 20,
        pageNumber = 1,
        sortColumn = null,
        searchTerm = "",
    }) => {
        const skip = (pageNumber - 1) * pageSize;
        let filter = null;

        // Build filter for username search
        if (searchTerm && searchTerm.trim()) {
            filter = `contains(userName,'${searchTerm.trim()}')`;
        }

        const url = buildApiV3UrlWithQuery(
            `iam-enablement/tenants/${tenantId}/accounts/${avaTaxAccountId}/users`,
            filter, // filter
            null, // include
            pageSize, // top
            skip, // skip
            sortColumn
        );
        const response = await axiosInstanceWithoutInterceptor.get(url, {
            withCredentials: true,
        });
        return {
            data: response.data,
            pageSize,
            pageNumber,
            skip,
            searchTerm,
        };
    },
    {
        condition: (_, { getState }) => {
            const state = getState();
            return !state.iamDs.users.isLoading;
        },
    }
);

export const enableBulkAccountsForIamDs = createAsyncThunk(
    "iamDs/enableBulkAccounts",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axiosInstanceWithoutInterceptor.post(
                buildApiV3Url("iam-enablement/accounts/bulk-job"),
                payload,
                {
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (err) {
            const data = err?.response?.data;
            const dataMessage = typeof data === "string" ? data : data?.message || data?.title;
            const errorMessage = dataMessage || "Failed to enable accounts";

            return rejectWithValue(errorMessage);
        }
    }
);

/**
 * Fetches job history based on filter criteria
 * @param {Object} payload - Filter criteria
 * @param {string} [payload.jobType] - Job type to filter by
 * @param {number} [payload.limit] - Number of jobs to retrieve
 * @param {string} [payload.externalJobId] - External job ID to search for
 * @param {number[]} [payload.jobIds] - Array of job IDs to fetch status for
 * @returns {Promise} Promise resolving to job data array
 */
export const fetchJobsHistoryBasedOnfilters = createAsyncThunk(
    "iamDs/fetchJobsHistoryBasedOnfilters",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axiosInstanceWithoutInterceptor.post(
                buildApiV3Url("iam-enablement/jobs/_query"),
                payload,
                {
                    withCredentials: true,
                }
            );
            return response.data;
        } catch (err) {
            const data = err?.response?.data;
            const dataMessage = typeof data === "string" ? data : data?.message || data?.title;
            const errorMessage = dataMessage || "Failed to fetch job history";

            return rejectWithValue(errorMessage);
        }
    }
);

export const enableAccountForIamDs = createAsyncThunk(
    "iamDs/enableAccount",
    async (avaTaxAccountId, { rejectWithValue }) => {
        try {
            const response = await axiosInstanceWithoutInterceptor.post(
                buildApiV3Url(`iam-enablement/accounts/${avaTaxAccountId}/jobs`),
                {
                    type: "account-enablement",
                },
                {
                    withCredentials: true,
                }
            );
            return {
                jobId: response.data,
                avaTaxAccountId,
            };
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            const dataMessage = typeof data === "string" ? data : data?.message;
            const notFoundMessage =
                "Account was not found. Please try with another or check the ID.";

            if (status === 404) {
                return rejectWithValue(notFoundMessage);
            }
            if (dataMessage && /account\s+was\s+not\s+found/i.test(dataMessage)) {
                return rejectWithValue(notFoundMessage);
            }
            return rejectWithValue(dataMessage || "Failed to enable account");
        }
    }
);

export const enableUsersForIamDs = createAsyncThunk(
    "iamDs/enableUsers",
    async (avaTaxAccountId, { rejectWithValue }) => {
        try {
            const response = await axiosInstanceWithoutInterceptor.post(
                buildApiV3Url(`iam-enablement/accounts/${avaTaxAccountId}/jobs`),
                {
                    type: "users-enablement",
                },
                {
                    withCredentials: true,
                }
            );
            return {
                jobId: response.data,
                avaTaxAccountId,
            };
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            const dataMessage = typeof data === "string" ? data : data?.message;
            const notFoundMessage =
                "Account was not found. Please try with another or check the ID.";

            if (status === 404) {
                return rejectWithValue(notFoundMessage);
            }
            if (dataMessage && /account\s+was\s+not\s+found/i.test(dataMessage)) {
                return rejectWithValue(notFoundMessage);
            }
            return rejectWithValue(dataMessage || "Failed to enable users");
        }
    }
);

export const fetchJobLogs = createAsyncThunk(
    "iamDs/fetchJobLogs",
    async ({ avaTaxAccountId, jobExternalId }) => {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3Url(`iam-enablement/accounts/${avaTaxAccountId}/jobs/${jobExternalId}/logs`),
            {
                withCredentials: true,
            }
        );
        return {
            logs: response.data,
            avaTaxAccountId,
            jobExternalId,
        };
    }
);

export const fetchTenantUserGrants = createAsyncThunk(
    "iamDs/fetchTenantUserGrants",
    async ({ tenantId, userId }) => {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3Url(`iam-enablement/tenants/${tenantId}/users/${userId}/grants`),
            {
                withCredentials: true,
            }
        );
        // Handle the API response structure with items array
        if (response.data?.items && Array.isArray(response.data.items)) {
            return response.data.items;
        }
        // Fallback for direct array response
        if (Array.isArray(response.data)) {
            return response.data;
        }
        return [];
    }
);

export const fetchUserScopeClaims = createAsyncThunk(
    "iamDs/fetchUserScopeClaims",
    async ({ subjectId }) => {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3Url(`iam-enablement/users/${subjectId}/scope-claims`),
            {
                withCredentials: true,
            }
        );
        const payload = response.data;
        if (Array.isArray(payload)) {
            return { subjectId, claims: payload };
        }
        if (payload && Array.isArray(payload.claims)) {
            return { subjectId: payload.subjectId || subjectId, claims: payload.claims };
        }
        return { subjectId, claims: [] };
    }
);

// Async thunk to assign grants to a user
export const assignGrantsToUser = createAsyncThunk(
    "iamDs/assignGrantsToUser",
    async ({ avataxAccountId, username }) => {
        const response = await axiosInstanceWithoutInterceptor.post(
            buildApiV3Url(
                `iam-enablement/accounts/${avataxAccountId}/users/${username}/assign-grants`
            ),
            null,
            {
                withCredentials: true,
            }
        );
        return response.data;
    }
);

// Async thunk to assign ECM aspect to a company
export const assignEcmAspect = createAsyncThunk(
    "iamDs/assignEcmAspect",
    async ({ tenantId, avaTaxAccountId, scsCompanyId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstanceWithoutInterceptor.post(
                buildApiV3Url(
                    `iam-enablement/tenants/${tenantId}/accounts/${avaTaxAccountId}/assign-scs-aspect?scsCompanyId=${scsCompanyId}`
                ),
                null,
                {
                    withCredentials: true,
                }
            );

            return {
                tenantId,
                avaTaxAccountId,
                scsCompanyId,
                response: response.data,
            };
        } catch (err) {
            const data = err?.response?.data;
            const dataMessage = typeof data === "string" ? data : data?.message;
            const errorMessage =
                dataMessage || "Unable to assign ECM aspect. Please try again later.";

            return rejectWithValue(errorMessage);
        }
    }
);

// Async thunk to assign ECM aspect to all remaining companies
export const assignEcmAspectBulk = createAsyncThunk(
    "iamDs/assignEcmAspectBulk",
    async ({ tenantId, avaTaxAccountId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstanceWithoutInterceptor.post(
                buildApiV3Url(
                    `iam-enablement/tenants/${tenantId}/accounts/${avaTaxAccountId}/assign-scs-aspect`
                ),
                null,
                {
                    withCredentials: true,
                }
            );

            return {
                tenantId,
                avaTaxAccountId,
                response: response.data,
            };
        } catch (err) {
            const data = err?.response?.data;
            const dataMessage = typeof data === "string" ? data : data?.message;
            const errorMessage =
                dataMessage ||
                "Unable to assign ECM aspect to all companies. Please try again later.";

            return rejectWithValue(errorMessage);
        }
    }
);

// Async thunk to remove grants from a user
export const removeGrantsFromUser = createAsyncThunk(
    "iamDs/removeGrantsFromUser",
    async ({ avataxAccountId, iamUserId, tenantId, systemId, userEmail }) => {
        const response = await axiosInstanceWithoutInterceptor.delete(
            buildApiV3Url(`iam-enablement/accounts/${avataxAccountId}/grants`),
            {
                data: {
                    iamUserId,
                    tenantId,
                    systemId,
                    userEmail,
                },
                headers: {
                    "Content-Type": "application/json",
                },
                withCredentials: true,
            }
        );
        return response.data;
    }
);

// Async thunk to toggle entitlement status
export const toggleEntitlementStatus = createAsyncThunk(
    "iamDs/toggleEntitlementStatus",
    async ({ entitlementId }) => {
        const response = await axiosInstanceWithoutInterceptor.put(
            buildApiV3Url(`iam-enablement/entitlements/${entitlementId}/status`),
            null,
            {
                withCredentials: true,
            }
        );
        return { entitlementId, data: response.data };
    }
);

export const iamDsSlice = createSlice({
    name: "iamDs",
    initialState,
    reducers: {
        clearAccounts: state => {
            state.accounts = {
                ...initialState.accounts,
            };
        },
        clearCompanies: state => {
            state.companies = {
                ...initialState.companies,
            };
        },
        clearAll: state => {
            state.accounts = initialState.accounts;
            state.companies = initialState.companies;
            state.users = initialState.users;
            state.enablement = initialState.enablement;
            state.jobLogs = initialState.jobLogs;
        },
        clearEnablement: state => {
            state.enablement = initialState.enablement;
        },
        clearUsersEnablement: state => {
            state.usersEnablement = initialState.usersEnablement;
        },
        clearJobLogs: state => {
            state.jobLogs = initialState.jobLogs;
        },
        clearUsers: state => {
            state.users = {
                ...initialState.users,
            };
        },
        setUsersSearchTerm: (state, action) => {
            state.users.searchTerm = action.payload;
        },
        clearUserGrants: state => {
            state.userGrants.data = [];
            state.userGrants.error = null;
        },
        clearUserScopeClaims: state => {
            state.userScopeClaims.data = { subjectId: null, claims: [] };
            state.userScopeClaims.error = null;
        },
        clearAssignGrants: state => {
            state.assignGrants = initialState.assignGrants;
        },
        clearAssignEcmAspect: state => {
            state.assignEcmAspect = initialState.assignEcmAspect;
        },
        clearAssignEcmAspectBulk: state => {
            state.assignEcmAspectBulk = initialState.assignEcmAspectBulk;
        },
        clearRemoveGrants: state => {
            state.removeGrants = initialState.removeGrants;
        },
        clearToggleEntitlement: state => {
            state.toggleEntitlement = initialState.toggleEntitlement;
        },
        updateJobsPagination: (state, action) => {
            state.jobs.paginate = {
                ...state.jobs.paginate,
                ...action.payload,
            };
        },
        updateJobsStatusFilter: (state, action) => {
            state.jobs.statusFilter = action.payload;
            // Reset to first page when filter changes
            state.jobs.paginate.pageNumber = 0;
            state.jobs.paginate.startIndex = 0;
            state.jobs.paginate.endIndex = state.jobs.paginate.rowsPerPage - 1;
        },
        clearJobs: state => {
            state.jobs = initialState.jobs;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchIamDsAccounts.pending, state => {
                state.accounts.isLoading = true;
                state.accounts.error = null;
            })
            .addCase(fetchIamDsAccounts.fulfilled, (state, action) => {
                state.accounts.isLoading = false;
                const rawItems = action.payload.items || [];
                const validatedItems = rawItems.map(item => ({
                    ...item,
                    tenantName: item.tenantName || "N/A",
                    tenantId: item.tenantId || "N/A",
                    entitlementId: item.entitlementId || "N/A",
                    avataxAccountId: item.avataxAccountId || "N/A",
                    certCaptureAccountId: item.certCaptureAccountId || "N/A",
                    organizationId: item.organizationId || "N/A",
                    systemId: item.systemId || "N/A",
                    initiatedBy: item.initiatedBy || "System",
                    initiatedAt: item.initiatedAt || null,
                    lastModified: item.lastModified || null,
                    features: Array.isArray(item.features) ? item.features : [],
                    isActive: item.entitlementStatus !== undefined ? item.entitlementStatus : true,
                }));
                state.accounts.data = validatedItems;
                state.accounts.totalCount = action.payload.totalCount || 0;
                state.accounts.error = null;
            })
            .addCase(fetchIamDsAccounts.rejected, (state, action) => {
                state.accounts.isLoading = false;
                state.accounts.error = action.error.message || "Failed to fetch IAM-DS accounts";
            })
            .addCase(fetchTenantCompanies.pending, state => {
                state.companies.isLoading = true;
                state.companies.error = null;
            })
            .addCase(fetchTenantCompanies.fulfilled, (state, action) => {
                state.companies.isLoading = false;
                const { data: response, pageSize, pageNumber, skip } = action.payload;

                const { items, totalCount } = parseApiResponse(response);

                state.companies.data = items;
                state.companies.recordsetCount = totalCount;
                state.companies.error = null;
                state.companies.paginate = {
                    pageNumber,
                    startIndex: skip,
                    endIndex: Math.max(skip + items.length - 1, skip),
                    rowsPerPage: pageSize,
                    totalRecords: totalCount,
                };
            })
            .addCase(fetchTenantCompanies.rejected, (state, action) => {
                state.companies.isLoading = false;
                state.companies.error = action.error.message || "Failed to fetch companies";
            })
            .addCase(fetchIamDsUsers.pending, state => {
                state.users.isLoading = true;
                state.users.error = null;
            })
            .addCase(fetchIamDsUsers.fulfilled, (state, action) => {
                state.users.isLoading = false;
                const payload = action.payload || {};
                const list = Array.isArray(payload?.value) ? payload.value : payload.items || [];
                state.users.data = list;
                state.users.recordsetCount =
                    payload.recordsetCount || payload.totalCount || list.length;
                state.users.error = null;
            })
            .addCase(fetchIamDsUsers.rejected, (state, action) => {
                state.users.isLoading = false;
                state.users.error = action.error.message || "Failed to fetch users";
            })
            .addCase(fetchTenantUsers.pending, state => {
                state.users.isLoading = true;
                state.users.error = null;
            })
            .addCase(fetchTenantUsers.fulfilled, (state, action) => {
                state.users.isLoading = false;
                const { data: response, pageSize, pageNumber, skip, searchTerm } = action.payload;

                const { items, totalCount } = parseApiResponse(response);
                const normalizedUsers = items.map(user => ({
                    subjectId: user.subjectId || "",
                    uid: user.id || "",
                    userName: user.username || "",
                    email: user.email || "",
                    status: user.active ? "Active" : "Inactive",
                    avataxSecurityRole: user.securityRoleId || "",
                }));

                state.users.data = normalizedUsers;
                state.users.recordsetCount = totalCount;
                state.users.searchTerm = searchTerm || "";
                state.users.error = null;
                state.users.paginate = {
                    pageNumber,
                    startIndex: skip,
                    endIndex: Math.max(skip + normalizedUsers.length - 1, skip),
                    rowsPerPage: pageSize,
                    totalRecords: totalCount,
                };
            })
            .addCase(fetchTenantUsers.rejected, (state, action) => {
                state.users.isLoading = false;
                state.users.error = action.error.message || "Failed to fetch users";
            })
            .addCase(enableAccountForIamDs.pending, state => {
                state.enablement.isLoading = true;
                state.enablement.error = null;
            })
            .addCase(enableAccountForIamDs.fulfilled, (state, action) => {
                state.enablement.isLoading = false;
                state.enablement.lastJobId = action.payload.jobId;
                state.enablement.lastAvaTaxAccountId = action.payload.avaTaxAccountId;
                state.enablement.error = null;
            })
            .addCase(enableAccountForIamDs.rejected, (state, action) => {
                state.enablement.isLoading = false;
                state.enablement.error =
                    action.payload || action.error.message || "Failed to enable account";
            })
            .addCase(enableBulkAccountsForIamDs.pending, state => {
                state.jobs.isLoading = true;
                state.jobs.error = null;
            })
            .addCase(enableBulkAccountsForIamDs.fulfilled, (state, action) => {
                state.jobs.isLoading = false;
                updateJobsWithPagination(state, action.payload);
            })
            .addCase(enableBulkAccountsForIamDs.rejected, (state, action) => {
                state.jobs.isLoading = false;
                state.jobs.error =
                    action.payload || action.error.message || "Failed to enable accounts";
            })
            .addCase(fetchJobsHistoryBasedOnfilters.pending, state => {
                state.jobs.isLoading = true;
                state.jobs.error = null;
            })
            .addCase(fetchJobsHistoryBasedOnfilters.fulfilled, (state, action) => {
                state.jobs.isLoading = false;
                updateJobsWithPagination(state, action.payload);
            })
            .addCase(fetchJobsHistoryBasedOnfilters.rejected, (state, action) => {
                state.jobs.isLoading = false;
                state.jobs.error = action.payload || action.error.message || "Failed to fetch jobs";
            })
            .addCase(enableUsersForIamDs.pending, state => {
                state.usersEnablement.isLoading = true;
                state.usersEnablement.error = null;
            })
            .addCase(enableUsersForIamDs.fulfilled, (state, action) => {
                state.usersEnablement.isLoading = false;
                state.usersEnablement.lastJobId = action.payload.jobId;
                state.usersEnablement.lastAvaTaxAccountId = action.payload.avaTaxAccountId;
                state.usersEnablement.error = null;
            })
            .addCase(enableUsersForIamDs.rejected, (state, action) => {
                state.usersEnablement.isLoading = false;
                state.usersEnablement.error =
                    action.payload || action.error?.message || "Failed to enable users";
            })
            .addCase(fetchJobLogs.pending, state => {
                state.jobLogs.isLoading = true;
                state.jobLogs.error = null;
            })
            .addCase(fetchJobLogs.fulfilled, (state, action) => {
                state.jobLogs.isLoading = false;
                state.jobLogs.data = action.payload;
                state.jobLogs.error = null;
            })
            .addCase(fetchJobLogs.rejected, (state, action) => {
                state.jobLogs.isLoading = false;
                state.jobLogs.error = action.error.message || "Failed to fetch job logs";
            })
            .addCase(fetchTenantUserGrants.pending, state => {
                state.userGrants.isLoading = true;
                state.userGrants.error = null;
            })
            .addCase(fetchTenantUserGrants.fulfilled, (state, action) => {
                state.userGrants.isLoading = false;
                state.userGrants.data = action.payload || [];
                state.userGrants.error = null;
            })
            .addCase(fetchTenantUserGrants.rejected, (state, action) => {
                state.userGrants.isLoading = false;
                state.userGrants.error = action.error.message || "Failed to fetch user grants";
            })
            .addCase(fetchUserScopeClaims.pending, state => {
                state.userScopeClaims.isLoading = true;
                state.userScopeClaims.error = null;
            })
            .addCase(fetchUserScopeClaims.fulfilled, (state, action) => {
                state.userScopeClaims.isLoading = false;
                state.userScopeClaims.data = action.payload;
                state.userScopeClaims.error = null;
            })
            .addCase(fetchUserScopeClaims.rejected, (state, action) => {
                state.userScopeClaims.isLoading = false;
                state.userScopeClaims.error =
                    action.error.message || "Failed to fetch user scope claims";
            })
            // Assign grants to user
            .addCase(assignGrantsToUser.pending, state => {
                state.assignGrants.isLoading = true;
                state.assignGrants.error = null;
                state.assignGrants.success = false;
            })
            .addCase(assignGrantsToUser.fulfilled, state => {
                state.assignGrants.isLoading = false;
                state.assignGrants.success = true;
                state.assignGrants.error = null;
            })
            .addCase(assignGrantsToUser.rejected, (state, action) => {
                state.assignGrants.isLoading = false;
                state.assignGrants.success = false;
                state.assignGrants.error =
                    action.error.message || "Failed to assign grants to user";
            })
            // Assign ECM aspect to company
            .addCase(assignEcmAspect.pending, (state, action) => {
                state.assignEcmAspect.isLoading = true;
                state.assignEcmAspect.error = null;
                state.assignEcmAspect.success = false;
                state.assignEcmAspect.companyId = action.meta.arg.scsCompanyId;
            })
            .addCase(assignEcmAspect.fulfilled, state => {
                state.assignEcmAspect.isLoading = false;
                state.assignEcmAspect.success = true;
                state.assignEcmAspect.error = null;
                state.assignEcmAspect.companyId = null;
            })
            .addCase(assignEcmAspect.rejected, (state, action) => {
                state.assignEcmAspect.isLoading = false;
                state.assignEcmAspect.success = false;
                state.assignEcmAspect.error = action.payload || "Failed to assign ECM aspect";
                state.assignEcmAspect.companyId = null;
            })
            // Assign ECM aspect to all companies (bulk)
            .addCase(assignEcmAspectBulk.pending, state => {
                state.assignEcmAspectBulk.isLoading = true;
                state.assignEcmAspectBulk.error = null;
                state.assignEcmAspectBulk.success = false;
            })
            .addCase(assignEcmAspectBulk.fulfilled, state => {
                state.assignEcmAspectBulk.isLoading = false;
                state.assignEcmAspectBulk.success = true;
                state.assignEcmAspectBulk.error = null;
            })
            .addCase(assignEcmAspectBulk.rejected, (state, action) => {
                state.assignEcmAspectBulk.isLoading = false;
                state.assignEcmAspectBulk.success = false;
                state.assignEcmAspectBulk.error =
                    action.payload || "Failed to assign ECM aspect to all companies";
            })
            // Remove grants from user
            .addCase(removeGrantsFromUser.pending, state => {
                state.removeGrants.isLoading = true;
                state.removeGrants.error = null;
                state.removeGrants.success = false;
            })
            .addCase(removeGrantsFromUser.fulfilled, state => {
                state.removeGrants.isLoading = false;
                state.removeGrants.success = true;
                state.removeGrants.error = null;
            })
            .addCase(removeGrantsFromUser.rejected, (state, action) => {
                state.removeGrants.isLoading = false;
                state.removeGrants.success = false;
                state.removeGrants.error =
                    action.error.message || "Failed to remove grants from user";
            })
            // Toggle entitlement status
            .addCase(toggleEntitlementStatus.pending, state => {
                state.toggleEntitlement.isLoading = true;
                state.toggleEntitlement.error = null;
                state.toggleEntitlement.success = false;
            })
            .addCase(toggleEntitlementStatus.fulfilled, (state, action) => {
                state.toggleEntitlement.isLoading = false;
                state.toggleEntitlement.success = true;
                state.toggleEntitlement.error = null;

                // Update the entitlement status in the accounts data
                const { entitlementId } = action.payload;
                const account = state.accounts.data.find(
                    acc => acc.entitlementId === entitlementId
                );
                if (account) {
                    account.isActive = !account.isActive;
                }
            })
            .addCase(toggleEntitlementStatus.rejected, (state, action) => {
                state.toggleEntitlement.isLoading = false;
                state.toggleEntitlement.success = false;
                state.toggleEntitlement.error =
                    action.error.message || "Failed to toggle entitlement status";
            });
    },
});

export const {
    clearAccounts,
    clearCompanies,
    clearAll,
    clearEnablement,
    clearUsersEnablement,
    clearJobLogs,
    clearUsers,
    setUsersSearchTerm,
    clearUserGrants,
    clearUserScopeClaims,
    clearAssignGrants,
    clearAssignEcmAspect,
    clearAssignEcmAspectBulk,
    clearRemoveGrants,
    clearToggleEntitlement,
    updateJobsPagination,
    updateJobsStatusFilter,
    clearJobs,
} = iamDsSlice.actions;

export const selectAccounts = state => state.iamDs.accounts;
export const selectJobs = state => state.iamDs.jobs;
export const selectJobsPagination = state => state.iamDs.jobs.paginate;
export const selectJobsStatusFilter = state => state.iamDs.jobs.statusFilter;
export const selectJobStatus = state => state.iamDs.jobStatus;
export const selectJobIds = state => state.iamDs.jobIds;
export const selectCompanies = state => state.iamDs.companies;
export const selectUsers = state => state.iamDs.users;
export const selectUserGrants = state => state.iamDs.userGrants;
export const selectUserScopeClaims = state => state.iamDs.userScopeClaims;
export const selectAssignGrants = state => state.iamDs.assignGrants;
export const selectAssignEcmAspect = state => state.iamDs.assignEcmAspect;
export const selectAssignEcmAspectBulk = state => state.iamDs.assignEcmAspectBulk;
export const selectRemoveGrants = state => state.iamDs.removeGrants;
export const selectToggleEntitlement = state => state.iamDs.toggleEntitlement;
export const selectEnablement = state => state.iamDs.enablement;
export const selectUsersEnablement = state => state.iamDs.usersEnablement;
export const selectJobLogs = state => state.iamDs.jobLogs;

export default iamDsSlice.reducer;
